import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  BookOpen,
  Video,
  Clock,
  Play,
  Star,
  TrendingUp,
  Home,
  ChevronRight,
  Award,
  Target,
  CheckCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getMyEnrollments } from '../../services/api';

const MyCourses = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  useEffect(() => {
    if (user) {
      fetchEnrollments();
    }
  }, [user]);

  const fetchEnrollments = async () => {
    try {
      setLoading(true);
      const response = await getMyEnrollments();
      setEnrollments(response.data.enrollments || []);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredEnrollments = () => {
    let filtered = [...enrollments];

    // Filter by search
    if (searchQuery) {
      filtered = filtered.filter(enrollment => {
        const course = enrollment.course;
        return course?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          course?.shortDescription?.toLowerCase().includes(searchQuery.toLowerCase());
      });
    }

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(enrollment => {
        const course = enrollment.course;
        return course?.courseType === filterType;
      });
    }

    // Sort
    switch (sortBy) {
      case 'recent':
        filtered.sort((a, b) => new Date(b.enrolledAt) - new Date(a.enrolledAt));
        break;
      case 'progress':
        filtered.sort((a, b) => (b.progressPercentage || 0) - (a.progressPercentage || 0));
        break;
      case 'title':
        filtered.sort((a, b) => (a.course?.title || '').localeCompare(b.course?.title || ''));
        break;
      default:
        break;
    }

    return filtered;
  };

  const getCourseStats = () => {
    const completed = enrollments.filter(e => e.progressPercentage === 100).length;
    const inProgress = enrollments.filter(e => e.progressPercentage > 0 && e.progressPercentage < 100).length;
    const notStarted = enrollments.filter(e => !e.progressPercentage || e.progressPercentage === 0).length;

    return { completed, inProgress, notStarted, total: enrollments.length };
  };

  const EnrollmentCard = ({ enrollment }) => {
    const course = enrollment.course;
    if (!course) return null;

    const progress = enrollment.progressPercentage || 0;
    const isCompleted = progress === 100;

    return (
      <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden transition-all duration-200 hover:shadow-lg group">
        <div className="aspect-[16/9] overflow-hidden relative">
          <img
            src={course.thumbnail || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=800&h=600&q=80'}
            alt={course.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
              {course.courseType === 'video' ? (
                <Play size={24} className="text-[#30C4B5] ml-1" />
              ) : (
                <BookOpen size={24} className="text-[#30C4B5]" />
              )}
            </div>
          </div>
          <div className="absolute top-3 left-3">
            <span className={`inline-flex items-center px-2 py-1 rounded text-white text-xs font-medium ${course.courseType === 'ebook' ? 'bg-blue-500' : 'bg-purple-500'
              }`}>
              {course.courseType === 'ebook' ? (
                <><BookOpen size={12} className="mr-1" /> Ebook</>
              ) : (
                <><Video size={12} className="mr-1" /> Video</>
              )}
            </span>
          </div>
          {isCompleted && (
            <div className="absolute top-3 right-3">
              <span className="inline-flex items-center px-2 py-1 rounded bg-green-500 text-white text-xs font-medium">
                <CheckCircle size={12} className="mr-1" />
                Completed
              </span>
            </div>
          )}
        </div>

        <div className="p-5">
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2 line-clamp-2">
            {course.title}
          </h3>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
            {course.shortDescription}
          </p>

          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-4 space-x-4">
            {course.totalLessons && (
              <div className="flex items-center">
                <BookOpen size={12} className="mr-1" />
                {course.totalLessons} lessons
              </div>
            )}
            {course.totalDuration && (
              <div className="flex items-center">
                <Clock size={12} className="mr-1" />
                {course.totalDuration}
              </div>
            )}
            {course.averageRating && (
              <div className="flex items-center">
                <Star size={12} className="mr-1 fill-yellow-400 text-yellow-400" />
                {course.averageRating.toFixed(1)}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Progress
              </span>
              <span className="text-xs font-medium text-gray-900 dark:text-white">
                {progress}%
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${isCompleted ? 'bg-green-500' : 'bg-[#30C4B5]'
                  }`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <button
              onClick={() => navigate(`/courses/${course._id}`)}
              className="w-full py-2 bg-[#30C4B5] hover:bg-[#29AF9F] text-white font-medium text-sm rounded-lg transition-colors flex items-center justify-center"
            >
              {isCompleted ? (
                <>
                  <Award size={16} className="mr-2" />
                  Review Course
                </>
              ) : progress > 0 ? (
                <>
                  <Play size={16} className="mr-2" />
                  Continue Learning
                </>
              ) : (
                <>
                  <Play size={16} className="mr-2" />
                  Start Learning
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return null;
  }

  const filteredEnrollments = getFilteredEnrollments();
  const stats = getCourseStats();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#000000]">
      <main className="max-w-[1400px] mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm mb-4">
            <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-[#30C4B5] transition-colors flex items-center">
              <Home size={14} className="mr-1" />
              Dashboard
            </Link>
            <ChevronRight size={14} className="text-gray-400" />
            <span className="text-gray-900 dark:text-white font-medium">My Courses</span>
          </nav>

          {/* Title */}
          <div className="mb-4">
            <h1 className="font-bold text-3xl text-gray-900 dark:text-white mb-2">
              My Learning Journey
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Track your progress and continue your trading education.
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-[#30C4B5] rounded-lg flex items-center justify-center mr-3">
                <BookOpen size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {stats.total}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Enrolled
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                <TrendingUp size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {stats.inProgress}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  In Progress
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                <CheckCircle size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {stats.completed}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Completed
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                <Target size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {stats.notStarted}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Not Started
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative md:col-span-2">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search my courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#30C4B5] focus:border-[#30C4B5] transition-all"
              />
            </div>

            {/* Type Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#30C4B5] focus:border-[#30C4B5] transition-all"
            >
              <option value="all">All Types</option>
              <option value="ebook">Ebooks</option>
              <option value="video">Video Courses</option>
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#30C4B5] focus:border-[#30C4B5] transition-all"
            >
              <option value="recent">Recently Enrolled</option>
              <option value="progress">By Progress</option>
              <option value="title">Alphabetical</option>
            </select>
          </div>
        </div>

        {/* Progress Summary */}
        {stats.total > 0 && (
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-black dark:to-black dark:!bg-black border border-cyan-200 dark:border-gray-800 rounded-xl p-6 mb-8">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-[#30C4B5] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <TrendingUp size={24} className="text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg text-cyan-800 dark:text-cyan-400 mb-2">
                  Keep Up The Great Work! 🎉
                </h3>
                <p className="text-sm text-cyan-700 dark:text-cyan-200 mb-4">
                  You're making excellent progress on your trading education journey. {stats.completed > 0 && `You've completed ${stats.completed} course${stats.completed > 1 ? 's' : ''}!`}
                </p>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-cyan-700 dark:text-cyan-200">{stats.completed} Completed</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-[#30C4B5] rounded-full mr-2"></div>
                    <span className="text-cyan-700 dark:text-cyan-200">{stats.inProgress} In Progress</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-gray-400 rounded-full mr-2"></div>
                    <span className="text-cyan-700 dark:text-cyan-200">{stats.notStarted} Not Started</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Courses Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6 animate-pulse">
                <div className="aspect-[16/9] bg-gray-200 dark:bg-gray-700 rounded-lg mb-4"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredEnrollments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEnrollments.map((enrollment) => (
              <EnrollmentCard key={enrollment._id} enrollment={enrollment} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800">
            <BookOpen size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2">
              {searchQuery || filterType !== 'all' ? 'No courses found' : 'No enrolled courses yet'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {searchQuery || filterType !== 'all'
                ? 'Try adjusting your search or filters.'
                : 'Start your learning journey by enrolling in courses.'}
            </p>
            {!searchQuery && filterType === 'all' && (
              <Link
                to="/courses"
                className="inline-flex items-center px-6 py-3 bg-[#30C4B5] hover:bg-[#29AF9F] text-white font-medium rounded-lg transition-colors"
              >
                Browse Courses
              </Link>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default MyCourses;
